import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// R159y · Feb 2026 — Task Finance popup. Opens from a $ icon on each
// task row while Accounts Review mode is on and the user has finance
// perms. Edits three otherwise-hidden fields on the task —
// `est_amount`, `paid_amount`, `currency` — and (optionally) posts a
// task-scope billing_note in one shot. Balance is auto-computed.
//
// Currency defaults to INR (₹). Users can flip a per-task currency to
// USD for tasks priced in dollars; roll-ups on the planning board are
// per-currency so mixed rows never sum ambiguously.
export const CURRENCIES = [
  { code: "INR", symbol: "₹", label: "Rupee (₹ INR)" },
  { code: "USD", symbol: "$", label: "Dollar ($ USD)" },
  { code: "EUR", symbol: "€", label: "Euro (€ EUR)" },
  { code: "GBP", symbol: "£", label: "Pound (£ GBP)" },
  { code: "AED", symbol: "AED", label: "Dirham (AED)" },
];
export const symbolFor = (code) => (CURRENCIES.find((c) => c.code === code)?.symbol) || (code || "₹");
export const fmtMoney = (amt, code) => {
  const n = Number(amt);
  const s = symbolFor(code);
  if (!Number.isFinite(n) || n === 0) return `${s} 0`;
  // Compact for large numbers to keep chips tight.
  const abs = Math.abs(n);
  const fmt = abs >= 100000
    ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(n / 100000) + "L"
    : abs >= 1000
      ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(n / 1000) + "K"
      : new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
  return `${s} ${fmt}`;
};

export default function TaskFinanceDialog({ open, wsId, task, onClose, onSaved }) {
  const [est, setEst] = useState("");
  const [paid, setPaid] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [noteBody, setNoteBody] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEst(task?.est_amount == null ? "" : String(task.est_amount));
    setPaid(task?.paid_amount == null ? "" : String(task.paid_amount));
    setCurrency(task?.currency || "INR");
    setNoteBody("");
  }, [open, task?.id, task?.est_amount, task?.paid_amount, task?.currency]);

  if (!task) return null;
  const estN = est === "" ? null : Number(est);
  const paidN = paid === "" ? null : Number(paid);
  const balance = (Number.isFinite(estN) ? estN : 0) - (Number.isFinite(paidN) ? paidN : 0);

  const save = async () => {
    setBusy(true);
    try {
      // Persist finance fields on the task. Empty strings clear the
      // value; non-numeric input is coerced to `null` so the DB row
      // stays clean (Pydantic strips them with exclude_none).
      const patch = { currency };
      patch.est_amount = est === "" ? null : (Number.isFinite(estN) ? estN : null);
      patch.paid_amount = paid === "" ? null : (Number.isFinite(paidN) ? paidN : null);
      await api.patch(`/tasks/${task.id}`, patch);
      // Optional task-scope accounts note.
      const trimmed = noteBody.trim();
      if (trimmed) {
        try {
          await api.post(`/workspaces/${wsId}/billing-notes`, {
            scope: "task", scope_id: task.id, body: trimmed,
          });
        } catch { toast.error("Task finance saved, but note couldn't be posted"); }
      }
      onSaved?.({ ...task, ...patch });
      onClose?.();
      toast.success("Task finance saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save task finance");
    } finally {
      setBusy(false);
    }
  };

  const sym = symbolFor(currency);
  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      {/* R159dd · Feb 2026 — Dropped the explicit z-[130] override.
          When this dialog is opened as a CHILD of the Finance Report
          the report used to bleed on top of the Edit-Task popup because
          both this dialog and the report set higher-than-default z. We
          now rely on Radix's DOM-portal order so whichever dialog was
          opened LAST is naturally on top, matching the standard modal
          stack behavior everywhere else in the app. */}
      <DialogContent className="max-w-md" data-testid="task-finance-dialog">
        <DialogHeader className="space-y-1 pb-2 border-b border-border">
          <DialogTitle className="text-base leading-tight">Task Finance</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-0.5">
              <div className="text-sm font-medium text-foreground truncate" data-testid="task-finance-title">
                {task.title || task.id}
              </div>
              {(task.client_name || task.project_name) && (
                <div className="text-[11px] text-muted-foreground truncate" data-testid="task-finance-breadcrumb">
                  {task.client_name || "No client"}
                  {task.project_name ? <> · <span className="opacity-80">{task.project_name}</span></> : null}
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Currency</span>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full h-9 border border-input bg-background px-2 rounded-none"
              data-testid="task-finance-currency"
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Estimated Amount ({sym})</span>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={est}
              onChange={(e) => setEst(e.target.value)}
              className="rounded-none"
              placeholder="0"
              data-testid="task-finance-est-input"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Paid Amount ({sym})</span>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={paid}
              onChange={(e) => setPaid(e.target.value)}
              className="rounded-none"
              placeholder="0"
              data-testid="task-finance-paid-input"
            />
          </label>
          <div className="flex items-center justify-between border-t border-border pt-2 mt-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Balance</span>
            <span
              className={`text-base font-mono ${balance > 0 ? "text-rose-700" : balance < 0 ? "text-emerald-700" : "text-neutral-600"}`}
              data-testid="task-finance-balance"
            >
              {sym} {new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(balance)}
            </span>
          </div>
          <label className="block pt-2 border-t border-border">
            <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Add accounts note (optional)</span>
            <Textarea
              rows={2}
              value={noteBody}
              onChange={(e) => setNoteBody(e.target.value)}
              placeholder="Send a private note to the Accounts team about this task…"
              className="rounded-none resize-none"
              data-testid="task-finance-note"
            />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()} disabled={busy} data-testid="task-finance-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="task-finance-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
